<template>
  <div class="live-map">
    <!-- left side controls panel -->
    <live-map-left-sidebar
      @update="applyFilter"
      :selected-vessel.sync="currentVessel.imo"
      :selected-vessel-details="selectedVesselDetails"
      class="live-map__controls"/>

    <!-- main map in the center -->
    <canvas-base-map
      class="live-map__map"
      :map-center="map.center"
      :map-zoom="map.zoom"
      :selected-vessel-imo="currentVessel.imo"
      :highlighted-vessel-imo="highlightedVesselIMO"
      :selected-vessel-details="selectedVesselDetails"
      :vessels-filter-parameters="liveVesselsApiData"
      :vessels-retrieving-method="getVesselsRetrievingMethod()"
      :tracking="tracking"
      :alerts="alerts.items"
      @show-tracks="showVesselTracks"
      @show-voyages="showVesselVoyages"
      @vessel-popup-open="vesselPopupOpen"
      @vessel-popup-close="vesselPopupClose"
      @map-ready="handleMapReady">
      <!-- controls: vessels quick search-->
      <template #control-top-right>
        <!-- a hack to prevent controls position changes when map is redrawn -->
        <vessel-search @input="handleVesselSelected($event.imo, 'search')"/>
      </template>

      <!-- controls: vessels stat and legend -->
      <template #control-bottom-left>
        <div class="vessels-info">
          <div class="vessels-stat map-overlay">
            <div class="vessels-stat__count">
              Total vessels: {{ totalVesselsCount || '--' }}
            </div>
            <div class="vessels-stat__count">
              Vessels in view: {{ vesselsInViewCount || '--' }}
            </div>
            <div class="vessels-stat__notes">
              Only active vessels with positions within last 30 days included.
            </div>
          </div>

          <div class="vessels-legend map-overlay">
            <p
              v-for="(item, index) in map.legend"
              :key="`legend-item-${index}`"
              class="vessels-legend__item">
              <span class="vessels-legend__icon-container">
                <img class="icon" :src="item.icon" :alt="item.title"/>
              </span>
              <span class="vessels-legend__text">{{ item.title }}</span>
            </p>
          </div>
        </div>
      </template>

      <!-- controls: vessel tracks-->
      <template #control-top-left>
        <vessel-track-management
          v-if="tracking.vessels.length"
          class="map-overlay"
          @input="applyTrackingFilter"
          @vessel-selected="handleVesselTrackSelected"
          @vessel-removed="handleVesselTrackRemoved"
          :show-points.sync="tracking.showPoints"
          :start-date="tracking.dateRange.startDate"
          :end-date="tracking.dateRange.endDate"
          :vessels="tracking.vessels"
          :palette="tracking.palette"
        />
      </template>

    </canvas-base-map>

    <!-- right details panel -->
    <live-map-vessel-details
      class="live-map__details"
      :expanded.sync="vesselDetails.expanded"
      :vessel="currentVessel.details"
    />
  </div>
</template>

<script>
import isEmpty from 'lodash-es/isEmpty';
import getNested from 'lodash-es/get';
import cloneDeep from 'lodash-es/cloneDeep';

import { mapActions, mapGetters } from 'vuex';
import CanvasBaseMap from '@/base_components/CanvasBaseMap';
import VesselSearch from '@/components/widgets/VesselSearch';
import { getVesselData } from '@/shared/api_formatters/vessels';
// import debounce from 'lodash-es/debounce';
import moment from 'moment';
import { trackingPalette } from '@/shared/constants/colors';
import VesselTrackManagement from '@/components/livemap/VesselTrackManagement';
import miniToastr from 'mini-toastr';
import { formatTrackingPoints, leafletBoundsToApi } from '@/shared/coordinate-utils';
import LiveMapVesselDetails from '@/components/livemap/LiveMapVesselDetails';
import LiveMapLeftSidebar from '@/components/livemap/LiveMapLeftSidebar';

// const debounceDelay = 40;

export default {
  name: 'live-map',

  components: {
    CanvasBaseMap,
    LiveMapLeftSidebar,
    LiveMapVesselDetails,
    VesselTrackManagement,
    VesselSearch,
  },

  data() {
    return {
      currentVessel: {
        imo: null,
        highlight: false,
        liveDetails: {},
        details: {},
      },

      vesselsCount: 0,
      apiFilterValues: {},  // apiFilterValues are updated on .applyFilter()

      map: {
        instance: null,
        bounds: {
          lowerLeft: [-180.0, -90.0],
          upperRight: [180.0, 90.0],
        },
        center: [47.41322, -1.219482],
        zoom: 3,
        defaultVesselZoom: 7,
        legend: [
          {
            title: 'Laden (underway)',
            icon: require('@/assets/icons/vessels/vessel-dark-arrow.svg'),
          },
          {
            title: 'Ballast (underway)',
            icon: require('@/assets/icons/vessels/vessel-light-arrow.svg'),
          },
          {
            title: 'Laden (still)',
            icon: require('@/assets/icons/vessels/vessel-dark-circle.svg'),
          },
          {
            title: 'Ballast (still)',
            icon: require('@/assets/icons/vessels/vessel-light-circle.svg'),
          },
        ],
      },

      vesselDetails: {
        expanded: null,
      },

      tracking: {
        showPoints: true,
        vessels: [],
        tracks: {}, // {<imo>: [point1, ...]}
        lines: {},
        dateRange: {
          startDate: this.getToday()
            .subtract(7, 'days')
            .format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD'),
        },
        palette: trackingPalette,
      },

      alerts: {
        items: [],
        dismissedAlertNames: new Set(),
      },

      maxVesselsAllowed: 500,
    };
  },

  methods: {
    ...mapActions('api/congestion', ['getAnchorageRegionsWithPolygons']),
    ...mapActions('api/fleets', ['searchVessels']),
    ...mapActions('api/livestates', ['getVesselsInArea', 'getVessels']),
    ...mapActions('map', ['updateTracking']),
    ...mapActions('api/vessels', [
      'getVesselLiveDetails',
      'getVesselPositions',
    ]),

    async applyFilter(apiFilterValues) {
      // console.log('applyFilter', JSON.stringify(apiFilterValues, null, 2));
      this.apiFilterValues = cloneDeep(apiFilterValues);
    },

    async handleMapReady(mapInstance) {
      this.map.instance = mapInstance;
      this.map.bounds = leafletBoundsToApi(mapInstance.getBounds());
    },


    getVesselsRetrievingMethod() {
      // vesselsRetrievingMethod is used in map store to retrieve vessels in tile components
      return async (apiData) => {
        const response = await this.getVesselsInArea({...this.liveVesselsApiData, ...apiData});
        return getNested(response, 'vessels', []).map(this.formatLiveStatesVessel);
      };
    },

    formatLiveStatesVessel(vessel) {
      let vesselFormatted = {
        ...vessel,
        position: {
          course: vessel.course,
          speed: vessel.speed,
        },
        state: {
          ladenStatus: vessel.ladenStatus === 'L' ? 'Laden' : 'Ballast',
        },
        static: {
          name: vessel.vesselName,
        },
      };

      const popupData = this.getVesselPopupData(vesselFormatted);
      const geometry = JSON.parse(vessel.geomCoordinates);
      delete vesselFormatted.geomCoordinates;
      return {
        ...vesselFormatted,
        popupData,
        geometry,
      };
    },

    getVesselPopupData(vessel) {
      const popupData = getVesselData(vessel);
      return {
        'IMO': popupData.imo,
        'Vessel name': popupData.vesselName,
        'DWT': '--',
        'Segment': popupData.segment,
        'Laden status': popupData.ladenStatus,
        'Destination': popupData.destination,
        'Draught': popupData.draught,
        '% of max draught': popupData.ladenPercent,
        'Speed': popupData.speed,
        // 'Course': vessel.course,
        'Updated': popupData.lastUpdate,
        'ETA': popupData.eta,
      };
    },

    async handleVesselSelected(vesselIMO, source) {
      // in case when a vessel is opened from "sidebar",
      // it's needed to open a required popup,
      // then this vessel will be selected by this "popupopen" event
      // it's done by base-map watcher on "selectedVesselImo"

      await this.loadCurrentVesselDetails(vesselIMO);

      if(this.currentVessel.liveDetails && source === 'search') {
        // source could be: search, sidebar, popup
        // move map center to the selected vessel
        this.showVesselOnMap(this.currentVessel.liveDetails);
      }
    },

    async loadCurrentVesselDetails(vesselIMO) {
      this.currentVessel.imo = vesselIMO;
      this.currentVessel.highlight = true;
      this.currentVessel.details = this.cachedVessels.get(vesselIMO) || {};
      this.currentVessel.liveDetails = await this.getVesselLiveDetails([vesselIMO]) || {};
    },

    showVesselOnMap(vessel) {
      if(!isEmpty(getNested(vessel, 'geometry.coordinates'))) {
        this.map.zoom = this.map.defaultVesselZoom;
        this.map.center = [
          getNested(vessel, 'geometry.coordinates.1', 0),
          getNested(vessel, 'geometry.coordinates.0', 0),
        ];
      }
    },

    async vesselPopupOpen(vesselIMO) {
      await this.handleVesselSelected(vesselIMO, 'popup');
      window.analytics.track('VesselSelectedOnLiveMap', {imo: vesselIMO});
    },

    vesselPopupClose() {
      this.currentVessel.highlight = false;
    },

    showVesselVoyages(vesselIMO) {
      this.currentVessel.imo = vesselIMO;
      this.vesselDetails.expanded = true;
    },

    async showVesselTracks(vesselIMO) {
      this.showVesselOnMap(this.cachedVessels.get(vesselIMO));  // show by cached coordinates if possible
      await this.loadCurrentVesselDetails(vesselIMO);
      const liveDetails = this.currentVessel.liveDetails;

      if(liveDetails) {
        // move map center to the selected vessel
        this.showVesselOnMap(liveDetails);

        if(!this.tracking.vessels.find(vessel => vesselIMO === vessel.imo)) {
          this.tracking.vessels.push(liveDetails);
          await this.applyTrackingFilter();
        }
      } else {
        miniToastr.warn(
          `"${liveDetails.vesselName} (${vesselIMO})"`,
          'Can\'t find the required vessel stats',
          10000,
        );
      }
    },

    async applyTrackingFilter(parameters) {
      if(parameters) {
        // parameters can be undefined when a vessel is selected in search multiselect
        this.tracking.vessels = cloneDeep(parameters.vessels);
        this.tracking.dateRange.startDate = parameters.dateRange.startDate;
        this.tracking.dateRange.endDate = parameters.dateRange.endDate;
      }

      for(let vessel of this.tracking.vessels) {
        let points = await this.getVesselTrack(vessel.imo);
        this.tracking.tracks[vessel.imo] = points;
        this.tracking.lines[vessel.imo] = formatTrackingPoints(points);
      }
      await this.updateTracking(this.tracking);  // map store action
    },

    async getVesselTrack(imo) {
      const data = {
        imo: imo,
        start_datetime: `${this.tracking.dateRange.startDate}T00:00:00`,
        end_datetime: `${this.tracking.dateRange.endDate}T23:59:59`,
      };

      let positions = await this.getVesselPositions(data);
      return positions.map(point => ({
        timestamp: moment(point.timestamp).format('YYYY-MM-DD HH:mm:ss'),
        position: [point.lat, point.lon],
      }));
    },

    handleVesselTrackSelected(vessel) {
      // popup will be opened when this imo will be available after an ".handleMapMove()"
      this.currentVessel.imo = vessel.imo;
      // show vessel details on sidebar
      this.$emit('vessel-selected', vessel);
      // move map center to the selected vessel
      this.showVesselOnMap(vessel);
    },

    handleVesselTrackRemoved(vessel) {
      delete this.tracking.tracks[vessel.imo];
      delete this.tracking.lines[vessel.imo];
      this.updateTracking(this.tracking);  // map store action
    },

    getPaletteColor(index) {
      const palette = this.tracking.palette;
      return palette[index % palette.length];
    },

    getToday() {
      return moment()
        .utc()
        .set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        });
    },
  },

  computed: {
    ...mapGetters('api', ['getActiveRequestsCount']),
    ...mapGetters('map', [
      'totalVesselsCount',
      'vesselsInViewCount',
    ]),

    cachedVessels() {
      return getNested(this.$store, 'state.map.vessels', new Map());
    },

    liveVesselsApiData() {
      const apiFilterValues = this.formattedAPIFilterValues;

      if(isEmpty(apiFilterValues)) {
        return {};
      } else {
        return {
          ...this.formattedAPIFilterValues,
          boundingBox: this.map.bounds,
          // limit: 3000,
        };
      }
    },

    highlightedVesselIMO() {
      if(this.currentVessel.highlight && this.currentVessel.imo) {
        return this.currentVessel.imo;
      } else {
        return null;
      }
    },

    selectedVesselDetails() {
      return {
        ...this.currentVessel.details,
        ...this.currentVessel.liveDetails,
      };
    },

    selectedVesselDetailsFormatted() {
      const vessel = this.selectedVesselDetails;
      return {
        imo: vessel.imo,
        geometry: vessel.geometry,
        position: {
          course: getNested(vessel, 'position.course', 0),
          speed: getNested(vessel, 'speed', 0),
        },
        state: {
          ladenStatus: vessel.ladenStatus === 'L' ? 'Laden' : 'Ballast',
        },
        static: {
          name: vessel.vesselName,
        },
      };
    },

    formattedAPIFilterValues() {
      if(isEmpty(this.apiFilterValues)) {
        return {};
      }

      let filterValues = cloneDeep(this.apiFilterValues);

      if(filterValues.enableEtaFilter) {
        filterValues.eta = [
          // Format of arrival_date_start and end must be YYYY-DD-MMTHH:MM (e.g. 2020-01-01T00:00)
          filterValues.arrivalDateStart + 'T00:00',
          filterValues.arrivalDateEnd + 'T00:00',
        ];
      }

      filterValues.ladenPercent[0] = filterValues.ladenPercent[0] / 100;
      filterValues.ladenPercent[1] = filterValues.ladenPercent[1] / 100;

      delete filterValues.arrivalDateStart;
      delete filterValues.arrivalDateEnd;
      delete filterValues.enableEtaFilter;

      return filterValues;
    },
  },
};
</script>

<style scoped lang="scss">
.live-map {
  display: flex;
  justify-content: stretch;
  margin: -30px -30px -30px -30px;
  height: calc(100vh - 109px);

  // left expandable column
  &__controls {
    margin-right: 1px;
  }

  // central map
  &__map {
    flex-grow: 2;
    height: calc(100vh - 109px);
  }

  // right expandable column
  &__details {
  }

  .vessels-info {
    max-width: 170px;

    // bottom left vessels count stats
    .vessels-stat {
      padding: 5px;
      border-radius: 5px;

      &__count {
        font-size: 14px;
      }

      &__notes {
        font-size: 11px;
      }
    }

    // bottom left legend
    .vessels-legend {
      margin-top: 10px;
      padding: 5px 10px 5px 0;
      border-radius: 5px;

      &__item {
        margin: 0;
        display: flex;
        justify-content: flex-start;
        align-items: center;
      }

      &__item:not(:last-child) {
        margin-bottom: 5px;
      }

      &__icon-container {
        width: 45px;
        text-align: center;

        img.icon {
          margin: -5px 0;
          transform: rotate(90deg);
          max-height: 25px;
          max-width: 12px;
        }
      }
    }
  }

  // top right vessel search input
  .vessel-search {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 500;
    transition: all 0.2s;
  }
}
</style>
